import React, { useEffect, useRef, useState } from "react"
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Link as MuiLink,
  Paper,
  Link,
  Collapse,
  Divider,
} from "@mui/material"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import PhoneIcon from "@mui/icons-material/Phone"
import EmailIcon from "@mui/icons-material/Email"
import LocationOnIcon from "@mui/icons-material/LocationOn"
import ExpandLess from "@mui/icons-material/ExpandLess"
import ExpandMore from "@mui/icons-material/ExpandMore"

import { useParams, useNavigate, Link as RouterLink } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"

import ContactForm from "../../components/ContactForm"
import PageBreadcrumb from "../../components/common/PageBreadcrumb"
import CallToAction from "../../components/CallToAction"
import Feedbacks from "../../components/service/Feedbacks"
import BlogsSection from "../../components/Blogs/BlogsSection"
import ServiceNotFound from "../../components/service/ServiceNotFound"
import {
  getServiceBySlug,
  clearService,
} from "../../features/services/serviceSlice"
import { getMegaMenuById, selectMegaMenu } from "../../features/megaMenuSlice"
import { siteContactConfig } from "../../config/siteConfig"
import { useMediaQuery, useTheme } from "@mui/material"

const IndustryDetail = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))

  const [openSections, setOpenSections] = useState({})

  const { industrySlug } = useParams()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const { phone, email, location, cta } = siteContactConfig

  /* ===== Mega Menu ===== */
  const { selected: megaMenu } = useSelector(selectMegaMenu)

  /* ===== Service ===== */
  const {
    service,
    status, // idle | pending | fulfilled | rejected
    loading: serviceLoading,
    error,
  } = useSelector((state) => state.services)

  /* ================= FETCH SERVICE ================= */
  useEffect(() => {
    if (industrySlug) {
      dispatch(getServiceBySlug(industrySlug))
    }
  }, [dispatch, industrySlug])

  /* ================= CLEAR SERVICE ON UNMOUNT ================= */
  useEffect(() => {
    return () => {
      dispatch(clearService())
    }
  }, [dispatch])

  /* ================= FETCH MEGA MENU ================= */
  useEffect(() => {
    if (service?.category_id) {
      dispatch(getMegaMenuById(service.category_id))
    }
  }, [dispatch, service?.category_id])

  /* ================= SAFE NAVIGATION ================= */

  const stats = [
    { value: "500+", label: "Clientele" },
    { value: "20+", label: "CA, CS & Lawyers" },
    { value: "3+", label: "Branches" },
  ]

  if (error) {
    return <ServiceNotFound></ServiceNotFound>
  }

  if (status === "pending") {
    return <Typography align='center'>Loading...</Typography>
  }

  const toggleSection = (index) => {
    setOpenSections((prev) => ({
      ...prev,
      [index]: !prev[index],
    }))
  }
  return (
    <Box>
      {/* ================= HERO SECTION ================= */}
      <Box sx={{ bgcolor: "#f5f9ff", py: { xs: 4, md: 8 } }}>
        <Container maxWidth='lg'>
          <Grid className='row' spacing={4} alignItems='stretch'>
            {/* LEFT CONTENT */}
            <Grid className='col-sm-7' item xs={12}>
              <PageBreadcrumb
                items={[
                  { label: "Home", to: "/" },
                  {
                    label: megaMenu?.title || "Industries",
                    to: `/services/${megaMenu?.menu_key}`,
                  },
                ]}
                current={service?.sort_title}
              />

              <Typography variant='h3' fontWeight={700} gutterBottom>
                {service?.sort_title}
              </Typography>

              <Typography color='text.secondary' mb={4}>
                {service?.description}
              </Typography>

              <Grid container spacing={3} mb={4}>
                {stats.map((item, index) => (
                  <Grid item xs={4} key={index}>
                    <Typography variant='h5' fontWeight={700}>
                      {item.value}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      {item.label}
                    </Typography>
                  </Grid>
                ))}
              </Grid>

              <Button
                variant='contained'
                size='large'
                onClick={() => navigate("/contact")}
              >
                Schedule Free Consultation
              </Button>
            </Grid>

            {/* RIGHT FORM */}
            <Grid className='col-sm-5' item xs={12}>
              <Paper sx={{ p: 4 }}>
                <ContactForm />
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ================= MAIN CONTENT ================= */}
      <Container maxWidth='lg' sx={{ py: 6 }}>
        <Grid className='row' spacing={4}>
          {/* LEFT SIDEBAR */}
          <Grid className='col-sm-4' item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography fontWeight={700} mb={2}>
                Related Services
              </Typography>
              <List>
                {megaMenu?.data?.sections?.map((section, sectionIndex) => (
                  <div key={section.title || sectionIndex}>
                    {/* Section header for mobile only */}
                    {isMobile && (
                      <ListItem
                        button
                        onClick={() => toggleSection(sectionIndex)}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <ListItemText
                          primary={section.heading}
                          primaryTypographyProps={{ fontWeight: 600 }}
                        />
                        {openSections[sectionIndex] ? (
                          <ExpandLess />
                        ) : (
                          <ExpandMore />
                        )}
                      </ListItem>
                    )}

                    {/* Collapsible items */}
                    <Collapse
                      in={isMobile ? !!openSections[sectionIndex] : true}
                      timeout='auto'
                      unmountOnExit
                    >
                      <List component='div' disablePadding>
                        {section.items.map((item) => (
                          <ListItem
                            key={item.link}
                            component={RouterLink}
                            to={item.link}
                            disableGutters
                            sx={{ pl: { xs: 4, md: 0 } }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon color='primary' />
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <MuiLink
                                  component={RouterLink}
                                  to={item.link}
                                  underline='none'
                                  color='inherit'
                                  sx={{ display: "inline" }}
                                >
                                  {item.label}
                                </MuiLink>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Collapse>
                  </div>
                ))}
              </List>
              <Divider sx={{ my: 2 }} />

              <Typography fontWeight={700} mb={2}>
                Contact Information
              </Typography>

              <Box display='flex' alignItems='center' mb={2}>
                <PhoneIcon sx={{ mr: 1 }} />
                <Typography component='a' href={phone.link}>
                  {phone.label}
                </Typography>
              </Box>

              <Box display='flex' alignItems='center' mb={2}>
                <EmailIcon sx={{ mr: 1 }} />
                <Typography component='a' href={email.link}>
                  {email.label}
                </Typography>
              </Box>

              <Box display='flex' alignItems='center'>
                <LocationOnIcon sx={{ mr: 1 }} />
                <Typography>{location.label}</Typography>
              </Box>

              <Button
                variant='contained'
                fullWidth
                sx={{ mt: 3 }}
                component='a'
                href={cta.link}
              >
                {cta.text}
              </Button>
            </Paper>
          </Grid>

          {/* CENTER CONTENT */}
          <Grid className='col-sm-8' item xs={12}>
            <Paper sx={{ p: 4, mb: 5 }}>
              <div
                dangerouslySetInnerHTML={{
                  __html: service?.article_content || "",
                }}
              />
            </Paper>
          </Grid>
        </Grid>
      </Container>
      <BlogsSection></BlogsSection>
      <Feedbacks></Feedbacks>
      <CallToAction></CallToAction>
    </Box>
  )
}

export default IndustryDetail
